import { OndeFicarWidget } from '@/components/OndeFicarWidget';
import { PromptAlignedFAQ } from '@/components/PromptAlignedFAQ';

export const frontmatter = {
  category: 'passeio',
  title: 'Surf na Ilha do Mel: Melhores Praias, Escolas e Preços 2026',
  excerpt:
    'Guia completo de surf na Ilha do Mel: melhores praias para surfar, escola Surfaholics, preços de aula e aluguel de prancha em 2026, campeonatos e dicas para iniciantes.',
  publishedAt: '2026-05-17',
  author: 'Guia Ilha do Mel',
  tags: ['surf', 'ilha-do-mel', 'praia-grande', 'escola-de-surf', 'esportes'],
  coverImage: '/images/sunset-beach.jpg',
  readingTime: '8 min',
};

# Surf na [Ilha do Mel](/): Melhores Praias, Escolas e Preços 2026

A Ilha do Mel é um dos poucos destinos do litoral paranaense onde você pode surfar de manhã, tomar banho em piscina natural de tarde e dançar forró de noite — tudo a pé. Para quem viaja com prancha ou quer experimentar o surf pela primeira vez, este guia tem tudo: **melhores praias, escola, preços atualizados para 2026 e o que esperar de cada ponto**.

---

## Melhores praias para surfar na Ilha do Mel

### 1. Praia Grande — o principal pico

A **Praia Grande** é o coração do surf na Ilha do Mel. Com 1,7 km de extensão e mar aberto para o Atlântico, ela recebe ondulação consistente ao longo do ano — especialmente no outono e inverno, quando os swells de sul chegam mais regulares.

| Informação        | Detalhe                                                |
| ----------------- | ------------------------------------------------------ |
| Localização       | 25 min de Nova Brasília                                |
| Tipo de onda      | Beach break, fundo de areia                            |
| Melhor pico       | Canto esquerdo (frente ao Grajagan Surf Resort)        |
| Nível recomendado | Intermediário a avançado no canto; iniciante no centro |
| Melhor época      | Outono e inverno (swell de sul)                        |

### 2. Praia de Fora (Nova Brasília) — para intermediários

A **Praia de Fora**, localizada entre dois costões rochosos a 15 minutos de Nova Brasília, tem ondas mais curtas e mais ocas que a Praia Grande. É um ponto querido pelos surfistas locais justamente por isso: mais radical, menos fila.

- **Destaque:** piscinas naturais na maré baixa para quem não está surfando
- **Atenção:** o costão rochoso exige cuidado extra — não é para iniciantes
- **Bônus:** o bar Astral da Ilha fica na saída da trilha

### 3. Praia de Fora das Encantadas (Praia da Gruta)

No lado oposto da ilha, a **Praia de Fora das Encantadas** é uma praia de mar aberto com ondas fortes e costão rochoso. É frequentada principalmente por surfistas que ficam no vilarejo de Encantadas.

- **Localização:** 10 min de Encantadas
- **Características:** ondas fortes, mar aberto, menos frequentada
- **Perfil:** surfistas experientes

### 4. Paralelas — o pico secreto

As **Paralelas** são uma formação rochosa ao norte da ilha, acessíveis principalmente de barco. Quando a swell encaixa, oferecem ondas de qualidade que pouquíssimos visitantes conhecem. Se você for surfista experiente e estiver na ilha por vários dias, vale conversar com os barqueiros sobre levar até lá.

### Resumo comparativo

| Praia                        | Nível                  | Acesso         | Tipo de onda        |
| ---------------------------- | ---------------------- | -------------- | ------------------- |
| Praia Grande (canto esq.)    | Intermediário/Avançado | 25 min de NB   | Beach break regular |
| Praia Grande (centro)        | Iniciante              | 25 min de NB   | Ondas menores       |
| Praia de Fora (NB)           | Intermediário          | 15 min de NB   | Curtas, mais ocas   |
| Praia de Fora das Encantadas | Avançado               | 10 min de Enc. | Fortes, rochosa     |
| Paralelas                    | Avançado               | Barco          | Quando encaixa      |

---

## Escola de surf: Surfaholics

A **Escola Surfaholics** é a referência de surf na Ilha do Mel, com parceria estabelecida com a **Pousada Astral da Ilha** em Nova Brasília. As aulas acontecem principalmente na Praia de Fora e na Praia Grande.

### Preços 2026

| Serviço                        | Preço         |
| ------------------------------ | ------------- |
| Aula de surf (prancha inclusa) | R$ 90/hora    |
| Aula de stand up paddle (SUP)  | R$ 120/hora   |
| Aluguel de prancha tradicional | R$ 25–50/hora |
| Aluguel de longboard           | R$ 30/hora    |
| Aluguel de SUP                 | R$ 50–60/hora |

> Dica: os valores são negociáveis para pacotes de múltiplas aulas. Se você pretende surfar por 3 dias, pergunte sobre pacotes — normalmente consegue desconto de 20-30%.

### O que inclui a aula

Uma aula padrão de 1 hora com a Surfaholics inclui:

- **Aquecimento em terra** com instrução de remada e posicionamento
- **Sessão no mar** com instrutor acompanhando e corrigindo
- **Prancha e leash** (não precisa levar nada)
- **Feedback** ao final

Para iniciantes absolutos, as primeiras duas aulas são as mais importantes — com bom instrutor, a maioria das pessoas fica em pé na prancha ainda na primeira sessão.

---

## Galo Surf Shop — onde comprar e consertar

A **Galo Surf Shop** fica no caminho para a Praia de Fora em Nova Brasília. É o ponto de referência para:

- Comprar acessórios (leash, parafina, óculos, protetor)
- Consertar pranchas (pequenos reparos em resina)
- Obter informações sobre condições do mar (os funcionários surfam e sabem das condições do dia)

---

## Campeonato Surf na Ilha

Todo mês de **março**, a Praia Grande recebe o **Campeonato "Surf na Ilha"**, tradicional evento do litoral paranaense que existe há vários anos.

| Informação | Detalhe                                      |
| ---------- | -------------------------------------------- |
| Época      | Março                                        |
| Local      | Praia Grande, Nova Brasília                  |
| Categorias | Juvenil, Open, Feminino, Master, SUP         |
| Acesso     | Gratuito para espectadores                   |
| Atmosfera  | Local, descontraído, com food trucks na orla |

Assistir às baterias da manhã (geralmente a partir das 8h) é uma experiência diferente — nada de arena, apenas a praia, os juízes numa barraca e as ondas do Atlântico.

---

## Dicas práticas para surfar na Ilha do Mel

### O que levar

- **Própria prancha:** pode ir no barco (avise o bilheteiro — cobram taxa extra na maioria das embarcações, aproximadamente R$ 15–30)
- **Leash:** obrigatório em mar com turistas; respeite a etiqueta
- **Parafina:** leve da cidade — a Galo Surf Shop vende, mas o estoque pode faltar na alta temporada
- **Protetor solar:** use o tipo reef safe — a Ilha do Mel tem área de preservação

### Etiqueta no pico

A Praia Grande tem pico local. Os moradores têm prioridade — isso é lei não escrita em qualquer surf break com comunidade local. Respeite, dê espaço, sorria. A vibe da ilha é descontraída, mas o pico funciona com hierarquia.

### Melhor horário

O **período da manhã (7h–10h)** é o melhor para surfar na ilha. O vento ainda está fraco, a maré costuma favorecer e o pico tem menos gente. A partir das 11h, o vento onshore começa a picotar a água.

### Verificar condições

Não há previsão de surf específica para a Ilha do Mel, mas você pode usar o **Windguru** ou **Surfline** para acompanhar o swell e o vento. Busque por "Paranaguá" ou "Pontal do Paraná" — as condições são similares.

---

## Surf para quem nunca surfou

A Ilha do Mel é um bom lugar para a **primeira aula de surf**, especialmente porque:

- Não tem carros nem o estresse de praias urbanas
- Os instrutores são locais que surfam essas ondas há anos
- O ambiente descontraído tira a pressão de iniciantes
- Após a aula, você pode relaxar na praia sem pressa

A **Praia Grande no centro** (longe do pico principal) tem ondas menores e mais previsíveis — o ambiente ideal para quem está tentando ficar em pé pela primeira vez.

---

<PromptAlignedFAQ
  faqs={[
    {
      q: 'Posso levar minha prancha para a Ilha do Mel?',
      a: 'Sim. As embarcações de Pontal do Sul transportam pranchas, mas cobram uma taxa adicional — geralmente R$ 15 a R$ 30. Avise o bilheteiro antes de embarcar. Na ilha, não há como alugar carrinho ou transporte; você carrega até a praia (25 min de caminhada para a Praia Grande).',
    },
    {
      q: 'Qual é a melhor época para surfar na Ilha do Mel?',
      a: 'Outono e inverno (abril a agosto). Os swells de sul chegam mais regulares e consistentes nessa época, com ondas de maior qualidade na Praia Grande. No verão (dezembro a fevereiro), o swell é mais variável — há bons dias, mas também longos períodos de calmaria.',
    },
    {
      q: 'Como agendar aula com a Escola Surfaholics?',
      a: 'A Surfaholics tem parceria com a Pousada Astral da Ilha em Nova Brasília. O agendamento pode ser feito diretamente na pousada ou, em alguns casos, por WhatsApp antes de viajar. Na alta temporada (janeiro, fevereiro, julho), o ideal é reservar com antecedência.',
    },
    {
      q: 'É possível surfar em Encantadas?',
      a: 'Sim. A Praia de Fora das Encantadas (também chamada de Praia da Gruta) tem ondas fortes de mar aberto e é frequentada por surfistas experientes. Não é para iniciantes — o costão rochoso exige conhecimento e cuidado. Também há opções de aluguel de SUP para aguas mais calmas próximas ao vilarejo.',
    },
  ]}
/>

---

<OndeFicarWidget />

Para planejar toda a viagem — não só o surf — acesse o Guia Completo da [Ilha do Mel](/). Tem roteiros, orçamento, onde comer e o que fazer em cada dia.
